import { FC, useEffect } from 'react';
import { toastr } from 'react-redux-toastr';
import { useIntl } from 'react-intl';
import { IClinicMember } from '../../types/clinic';
import { UserPermissionEnum } from '../../constants/enums';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  useDeleteClinicInviteMutation,
  useDeleteClinicUserMutation,
  useSendClinicInviteMutation,
} from '../../redux/api/clinic/clinicApi';
import Spinner from '../Shared/Loaders/Spinner';
import styles from './UsersTabForms.module.scss';
import Modal from '../Shared/Modal/Modal';
import cn from 'classnames';

interface FormValues {
  email: string;
  permission: string;
}

interface Props {
  user?: IClinicMember;
  isOpen: boolean;
  userGroupId: string;
  onDialogCloseClick: () => void;
}

const EditUserForm: FC<Props> = ({ user, isOpen, userGroupId, onDialogCloseClick }) => {
  const intl = useIntl();
  const [inviteMember, { isLoading: isInviting }] = useSendClinicInviteMutation();
  const [deleteUser, { isLoading: isUserDeleting }] = useDeleteClinicUserMutation();
  const [deleteInvite, { isLoading: isInviteDeleting }] = useDeleteClinicInviteMutation();

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: user,
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    console.log(data);
    onDialogCloseClick();
    reset();
  };

  useEffect(() => {
    reset(user);
  }, [user, reset]);

  const resendLinkHandler = async () => {
    if (!user) return;
    const res = await inviteMember({
      email: user.email,
      permission: user.permission,
      userGroupId,
    });
    if ('error' in res) return;
    onDialogCloseClick();
    toastr.success(
      intl.formatMessage({ id: 'general.success', defaultMessage: 'Success' }),
      intl.formatMessage({
        id: 'implant.inviteSuccess',
        defaultMessage: 'The invitation was sent successfully',
      }),
      { showCloseButton: false }
    );
    console.log(res);
  };

  const deleteUserHandler = async () => {
    await deleteUser({ userGroupId, userId: user?.id });
    onDialogCloseClick();
    reset();
  };

  const deleteInviteHandler = async () => {
    const res = await deleteInvite({ userGroupId: userGroupId, email: user ? user.email : '' });
    if ('error' in res && 'data' in res.error) {
      console.log(res);
      toastr.error(
        intl.formatMessage({ id: 'general.error', defaultMessage: 'Error' }),
        intl.formatMessage({
          id: 'general.requestProblem',
          defaultMessage: 'There was a problem with your request.',
        }),
        { showCloseButton: false }
      );
      return;
    }
    toastr.success(
      intl.formatMessage({ id: 'general.success', defaultMessage: 'Success' }),
      intl.formatMessage({
        id: 'users.deleteInviteSuccess',
        defaultMessage: 'The invitation was deleted successfully',
      }),
      { showCloseButton: false }
    );

    onDialogCloseClick();
    reset();
  };

  const isLoading = isInviteDeleting || isUserDeleting || isInviting;

  return (
    <Modal
      fullModal={true}
      header={
        user?.id
          ? intl.formatMessage({ id: 'users.editUser', defaultMessage: 'Edit user' })
          : intl.formatMessage({
              id: 'users.editInvite',
              defaultMessage: 'Edit invite',
            })
      }
      onDismiss={onDialogCloseClick}
      show={isOpen}
      modalContentClass={styles.modalContent}
    >
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.formInfo}>
          {user?.id
            ? intl.formatMessage({
                id: 'users.editUserFormInfo',
                defaultMessage: 'Edit the selected users permission. Click save when you’re done. ',
              })
            : intl.formatMessage({
                id: 'users.editInviteFormInfo',
                defaultMessage: 'Edit the selected invite. Click save when you’re done. ',
              })}
        </div>
        <div className={styles.formItem}>
          <label htmlFor="email">
            {intl.formatMessage({
              id: 'general.email',
              defaultMessage: 'Email',
            })}
          </label>
          <input
            disabled
            type="email"
            id="email"
            placeholder={intl.formatMessage({
              id: 'users.emailPlaceholder',
              defaultMessage: 'Enter email',
            })}
            className={`${errors.email && styles.error}`}
            {...register('email', { required: 'Please enter email' })}
          />
          {errors.email && <p className={styles.helperText}>{errors.email.message}</p>}
        </div>
        <div className={styles.formItem}>
          <label htmlFor="userPermission">
            {intl.formatMessage({
              id: 'general.userPermission',
              defaultMessage: 'User permission',
            })}
          </label>
          <select
            disabled
            id="userPermission"
            placeholder={intl.formatMessage({
              id: 'users.userPermissionPlaceholder',
              defaultMessage: 'Select user permission',
            })}
            className={`${errors.permission && styles.error}`}
            {...register('permission', {
              required: 'Please select user permission',
            })}
          >
            <option value={UserPermissionEnum.USER}>
              {intl.formatMessage({
                id: 'general.user',
                defaultMessage: 'User',
              })}
            </option>
            <option value={UserPermissionEnum.ADMIN}>
              {intl.formatMessage({
                id: 'general.admin',
                defaultMessage: 'Admin',
              })}
            </option>
          </select>
          {errors.permission && <p className={styles.helperText}>{errors.permission.message}</p>}
        </div>
        {!user?.id && (
          <div className="formItem">
            <button
              type="button"
              disabled={isLoading}
              className={styles.btnLink}
              onClick={resendLinkHandler}
            >
              {intl.formatMessage({
                id: 'users.resendInvitationLink',
                defaultMessage: 'Resend invitation link',
              })}
            </button>
          </div>
        )}
        <div className={styles.editFormAction}>
          <button
            type="button"
            disabled={isLoading}
            className={cn(styles.btn, styles.btnDestructive)}
            onClick={user?.id ? deleteUserHandler : deleteInviteHandler}
          >
            {isUserDeleting ? (
              <Spinner destructive />
            ) : user?.id ? (
              intl.formatMessage({ id: 'users.deleteUser', defaultMessage: 'Delete user' })
            ) : (
              intl.formatMessage({ id: 'users.deleteInvite', defaultMessage: 'Delete invite' })
            )}
          </button>

          <div className={styles.formAction}>
            <button
              type="button"
              disabled={isLoading}
              className={cn(styles.btn, styles.btnOutline)}
              onClick={onDialogCloseClick}
            >
              {intl.formatMessage({ id: 'general.cancel', defaultMessage: 'Cancel' })}
            </button>

            <button
              type="submit"
              className={cn(styles.btn, styles.btnPrimary)}
              disabled={isLoading}
            >
              {intl.formatMessage({ id: 'general.save', defaultMessage: 'Save' })}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default EditUserForm;
